import React, { useState, useRef } from 'react';
import { Camera, Image as ImageIcon, Plus, Minus, Equal, Receipt } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';

/**
 * PM Spec V3 13/05/2026 — QuickWeightInput
 * Champ poids constaté avec :
 *   - Bouton "= Identique au prévu" (1-tap)
 *   - Boutons +/- 0.5 / +/- 1
 *   - Photo ticket pesée alternative (callback onPhotoTicket)
 *
 * Props :
 *   value, onChange, expectedWeight (poids prévu en T)
 *   onPhotoTicket(file) — callback si l'utilisateur ajoute une photo de ticket
 */
const QuickWeightInput = ({
  value, onChange,
  expectedWeight,
  onPhotoTicket,
  testId = 'quick-weight-input',
}) => {
  const fileRef = useRef(null);
  const [hasTicket, setHasTicket] = useState(false);

  const v = parseFloat(value) || 0;
  const exp = parseFloat(expectedWeight) || 0;

  const adjust = (delta) => {
    const next = Math.max(0, parseFloat(((v + delta) || 0).toFixed(2)));
    onChange?.(String(next));
  };

  const setFromExpected = () => onChange?.(String(exp));

  const handleFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setHasTicket(true);
    onPhotoTicket?.(f);
  };

  return (
    <div className="space-y-2" data-testid={testId}>
      <div className="flex items-center gap-2">
        <Input
          type="number" inputMode="decimal" step="0.1"
          data-testid={`${testId}-input`}
          value={value || ''}
          onChange={(e) => onChange?.(e.target.value)}
          placeholder={exp ? `Prévu : ${exp} T` : 'Poids en T'}
          className="h-12 text-lg"
        />
        <span className="text-slate-500 text-sm">T</span>
      </div>

      <div className="flex flex-wrap gap-2">
        {exp > 0 && (
          <Button
            type="button" variant="outline" size="sm"
            data-testid={`${testId}-eq-expected`}
            onClick={setFromExpected}
            className="gap-1.5 bg-emerald-50 border-emerald-200 hover:bg-emerald-100"
          >
            <Equal className="w-3.5 h-3.5 text-emerald-700" />
            <span className="font-medium">= Prévu ({exp} T)</span>
          </Button>
        )}
        <Button
          type="button" variant="outline" size="sm"
          data-testid={`${testId}-plus-half`}
          onClick={() => adjust(0.5)} className="gap-1"
        >
          <Plus className="w-3 h-3" />0,5
        </Button>
        <Button
          type="button" variant="outline" size="sm"
          data-testid={`${testId}-minus-half`}
          onClick={() => adjust(-0.5)} className="gap-1"
        >
          <Minus className="w-3 h-3" />0,5
        </Button>
        <Button
          type="button" variant="outline" size="sm"
          data-testid={`${testId}-plus-one`}
          onClick={() => adjust(1)} className="gap-1"
        >
          <Plus className="w-3 h-3" />1
        </Button>
        <Button
          type="button" variant="outline" size="sm"
          data-testid={`${testId}-minus-one`}
          onClick={() => adjust(-1)} className="gap-1"
        >
          <Minus className="w-3 h-3" />1
        </Button>
      </div>

      <div>
        <input
          ref={fileRef} type="file" accept="image/*" capture="environment"
          onChange={handleFile} className="hidden"
          data-testid={`${testId}-ticket-file`}
        />
        <Button
          type="button" variant="ghost" size="sm"
          data-testid={`${testId}-ticket-btn`}
          onClick={() => fileRef.current?.click()}
          className="gap-1.5 text-slate-600 hover:text-emerald-700"
        >
          <Receipt className="w-3.5 h-3.5" />
          {hasTicket ? '✓ Ticket pesée joint' : 'Photo du ticket de pesée'}
        </Button>
      </div>
    </div>
  );
};

export default QuickWeightInput;
